import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import SEO from "@/components/SEO";
import { api, extractError } from "@/lib/api";
import { SITTER_DETAIL, BOOKING } from "@/constants/testIds";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  BadgeCheck, MapPin, DollarSign, Star, CalendarClock, MessageSquare,
  Loader2, GraduationCap, Globe, ShieldCheck, Sparkles, Baby, Users
} from "lucide-react";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

export default function SitterDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sitter, setSitter] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewsGated, setReviewsGated] = useState(false);
  const [reviewsGatedMsg, setReviewsGatedMsg] = useState("");
  const [children, setChildren] = useState([]);

  // booking form
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [selectedKids, setSelectedKids] = useState([]);
  const [notes, setNotes] = useState("");
  const [booking, setBooking] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const [pattern, setPattern] = useState("weekly");
  const [occurrences, setOccurrences] = useState(4);
  const [joiningWaitlist, setJoiningWaitlist] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, r] = await Promise.all([
          api.get(`/sitters/${id}`),
          api.get(`/sitters/${id}/reviews`),
        ]);
        setSitter(s.data);
        setReviews(r.data.results);
        setReviewsGated(!!r.data.gated);
        setReviewsGatedMsg(r.data.message || "");
        if (user?.role === "family") {
          try {
            const c = await api.get("/children");
            setChildren(c.data);
          } catch (e) { console.warn("[SitterDetail] children fetch failed", e); }
        }
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 402 && d?.code === "profile_view_cap") {
        if (window.confirm(`${d.message}\n\nView pricing?`)) window.location.href = "/pricing";
        navigate(-1);
        return;
      }
      toast.error(extractError(err)); navigate(-1);
    }
  };
    load();
  }, [id, user, navigate]);

  const submitBooking = async (e) => {
    e.preventDefault();
    if (!start || !end) return toast.error("Choose start and end times");
    await doBooking(false);
  };

  const doBooking = async (force) => {
    setBooking(true);
    try {
      const startISO = new Date(start).toISOString();
      const endISO = new Date(end).toISOString();
      const payload = {
        sitter_id: id, child_ids: selectedKids, start_at: startISO, end_at: endISO, notes,
        force,
      };
      if (isRecurring) {
        payload.recurring = true;
        payload.recurrence = { pattern, occurrences: Number(occurrences) };
      }
      const { data } = await api.post("/bookings", payload);
      const created = data.series?.length || 1;
      toast.success(isRecurring ? `${created} recurring bookings requested` : "Booking request sent!");
      navigate("/bookings", { state: { highlight: data.booking?.id } });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code === "overlap") {
        const conflictLines = (detail.conflicts || []).slice(0, 4).map(
          (c) => `• ${new Date(c.start_at).toLocaleString()} — ${new Date(c.end_at).toLocaleString()} (${c.status})`
        ).join("\n");
        const ok = window.confirm(
          `This sitter already has overlapping bookings:\n\n${conflictLines}\n\nSend your request anyway?`
        );
        if (ok) await doBooking(true);
      } else if (err?.response?.status === 402 && detail?.code === "sitter_free_last_minute") {
        if (window.confirm(`${detail.message}\n\nView pricing?`)) window.location.href = "/pricing";
      } else {
        toast.error(extractError(err));
      }
    }
    finally { setBooking(false); }
  };

  const joinWaitlist = async () => {
    if (!start || !end) return toast.error("Choose preferred start and end times first");
    setJoiningWaitlist(true);
    try {
      await api.post("/waitlist", {
        sitter_id: id,
        preferred_start: new Date(start).toISOString(),
        preferred_end: new Date(end).toISOString(),
        child_ids: selectedKids, notes,
      });
      toast.success("Added to sitter's waitlist");
    } catch (err) { toast.error(extractError(err)); }
    finally { setJoiningWaitlist(false); }
  };

  const startChat = async () => {
    if (!sitter) return;
    try {
      await api.post("/messages", { to_user_id: sitter.user_id, body: `Hi ${sitter.name}, I'd love to chat about care for my family.` });
      navigate(`/messages?to=${sitter.user_id}`);
    } catch (err) { toast.error(extractError(err)); }
  };

  if (!sitter) return (<div className="min-h-screen bg-stone2-50"><Navbar /><div className="p-10 text-stone2-500">Loading…</div></div>);

  const hours = (start && end) ? Math.max(0, (new Date(end) - new Date(start)) / 3600000) : 0;
  const subtotal = hours * (sitter.hourly_rate || 0);
  const fee = subtotal * 0.10;
  const total = subtotal + fee;

  const respiteLabels = {
    traditional_respite: "traditional respite",
    overnight_support: "overnight support",
    travel_assistance: "travel assistance",
    party_buffers: "party buffer support",
    community_support: "community support",
  };
  const services = (sitter.respite_services || []).map((s) => respiteLabels[s] || s).join(", ");
  const seoTitle = `${sitter.name || "Autism babysitter"} — Specialized sitter${services ? ` for ${services}` : ""} | AutismBabysitters`;
  const seoDesc = `${sitter.name || "This sitter"}: ${(sitter.biography || "Specialized autism sitter available on AutismBabysitters.").slice(0, 140)}${sitter.hourly_rate ? ` From $${sitter.hourly_rate}/hr.` : ""}${sitter.city ? ` Based in ${sitter.city}${sitter.state ? ", " + sitter.state : ""}.` : ""}`;
  const seoCanonical = `https://autismbabysitters.com/sitters/${sitter.id}`;
  const sitterJsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": sitter.name,
    "jobTitle": "Specialized autism sitter",
    "description": sitter.biography || undefined,
    "knowsLanguage": sitter.languages || undefined,
    "hasCredential": (sitter.certifications || []).map((c) => ({ "@type": "EducationalOccupationalCredential", "name": c })),
    "aggregateRating": sitter.rating_count > 0 ? {
      "@type": "AggregateRating",
      "ratingValue": sitter.rating_avg,
      "reviewCount": sitter.rating_count,
    } : undefined,
    "url": seoCanonical,
  };

  return (
    <div className="min-h-screen bg-stone2-50" data-testid={SITTER_DETAIL.page}>
      <SEO title={seoTitle} description={seoDesc} canonical={seoCanonical} jsonLd={sitterJsonLd} />
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-10 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <section className="paper-card p-6">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-full bg-sage-600 text-white flex items-center justify-center font-heading font-bold text-2xl">
                  {sitter.name?.[0]?.toUpperCase() || "?"}
                </div>
                <div>
                  <h1 className="text-2xl font-heading font-bold text-sage-900 flex items-center gap-2">
                    {sitter.name}
                    {sitter.verified && <span className="chip"><BadgeCheck className="w-3.5 h-3.5 mr-1" /> Verified</span>}
                  </h1>
                  <div className="mt-1 text-sm text-stone2-600 flex flex-wrap items-center gap-3">
                    <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {sitter.city}, {sitter.state}</span>
                    <span className="flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /> ${sitter.hourly_rate}/hr</span>
                    <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5 text-ochre-500 fill-ochre-500" /> {sitter.rating_avg || "—"} ({sitter.rating_count || 0})</span>
                    <span className="flex items-center gap-1"><GraduationCap className="w-3.5 h-3.5" /> {sitter.experience_years} yrs</span>
                  </div>
                </div>
              </div>
            </div>
            <p className="mt-5 text-stone2-700 leading-relaxed">{sitter.biography || "This sitter hasn't added a bio yet."}</p>
          </section>

          <section className="paper-card p-6">
            <h2 className="font-heading font-semibold text-sage-900 mb-3 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-sage-600" /> Credentials
            </h2>            <div className="flex flex-wrap gap-1.5">
              {(sitter.certifications || []).map((c) => <span key={c} className="chip">{c}</span>)}
              {sitter.aba_trained && <span className="chip-ochre"><Sparkles className="w-3 h-3 mr-1" /> ABA trained</span>}
              {sitter.special_ed_experience && <span className="chip-ochre">Special ed</span>}
              {sitter.non_smoker && <span className="chip">Non-smoker</span>}
              {sitter.can_transport && <span className="chip">Can transport</span>}
              {sitter.comfortable_aggressive_behaviors && <span className="chip">Aggressive behaviors OK</span>}
              {sitter.comfortable_toileting && <span className="chip">Toileting OK</span>}
              {sitter.overnight_care && <span className="chip">Overnight</span>}
              {sitter.travel_support && <span className="chip">Travel</span>}
            </div>
            {(sitter.respite_services || []).length > 0 && (
              <div className="mt-4">
                <div className="field-label">Respite services offered</div>
                <div className="flex flex-wrap gap-1.5" data-testid="sitter-detail-respite">
                  {sitter.respite_services.map((svc) => {
                    const map = {
                      traditional_respite: "Traditional respite",
                      overnight_support: "Overnight support",
                      travel_assistance: "Travel assistance",
                      party_buffers: "Party buffers",
                      community_support: "Community support",
                    };
                    return <span key={svc} className="chip-ochre">{map[svc] || svc}</span>;
                  })}
                </div>
              </div>
            )}
            {sitter.education && <p className="mt-4 text-sm text-stone2-700"><span className="field-label">Education</span>{sitter.education}</p>}
            {sitter.experience_description && <p className="mt-4 text-sm text-stone2-700"><span className="field-label">Experience</span>{sitter.experience_description}</p>}
            <div className="mt-4 flex items-center gap-2 text-sm text-stone2-700">
              <Globe className="w-4 h-4 text-sage-600" />
              {(sitter.languages || []).join(", ")}
            </div>
          </section>

          <section className="paper-card p-6">
            <h2 className="font-heading font-semibold text-sage-900 mb-3">Weekly availability</h2>
            <div className="grid sm:grid-cols-2 gap-2">
              {DAYS.map((d) => (
                <div key={d} className="rounded-lg bg-stone2-50 p-3 text-sm">
                  <div className="capitalize font-semibold text-sage-800">{d}</div>
                  <div className="text-stone2-600 mt-1">
                    {(sitter.availability?.[d] || []).length
                      ? sitter.availability[d].join(" · ")
                      : <span className="text-stone2-400">unavailable</span>}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="paper-card p-6">
            <h2 className="font-heading font-semibold text-sage-900 mb-3">Reviews</h2>
            {reviewsGated && (
              <div className="rounded-lg bg-ochre-50 border border-ochre-200 p-4 text-sm text-ochre-900" data-testid="reviews-gated-banner">
                <div className="font-semibold">Reviews are only visible to Enhanced sitters</div>
                <div className="mt-1">{reviewsGatedMsg}</div>
                <a href="/pricing" className="mt-2 inline-block btn-accent text-xs">See pricing</a>
              </div>
            )}
            {!reviewsGated && reviews.length === 0 && <p className="text-sm text-stone2-500">No reviews yet.</p>}

            {sitter.rating_breakdown && Object.values(sitter.rating_breakdown).some((v) => v > 0) && (
              <div className="mb-5 grid sm:grid-cols-2 gap-3 rounded-lg bg-sage-50 p-4" data-testid="sitter-rating-breakdown">
                {[
                  ["professionalism", "Professionalism"],
                  ["communication", "Communication"],
                  ["child_engagement", "Child engagement"],
                  ["reliability", "Reliability"],
                  ["behavioral_knowledge", "Behavioral knowledge"],
                ].map(([k, l]) => {
                  const val = sitter.rating_breakdown[k] || 0;
                  return (
                    <div key={k}>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-stone2-600">{l}</span>
                        <span className="font-semibold text-sage-900">{val.toFixed(1)}</span>
                      </div>
                      <div className="mt-1 h-1.5 bg-white rounded-full overflow-hidden">
                        <div className="h-full bg-sage-600" style={{ width: `${(val / 5) * 100}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <ul className="space-y-4">
              {reviews.map((r) => (
                <li key={r.id} className="border-b border-stone2-200 pb-4 last:border-0" data-testid={`review-${r.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-sage-900 text-sm">{r.family_name}</div>
                    <div className="flex items-center gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? "text-ochre-500 fill-ochre-500" : "text-stone2-200"}`} />
                      ))}
                    </div>
                  </div>
                  <p className="mt-1 text-sm text-stone2-700">{r.comment}</p>
                </li>
              ))}
            </ul>
          </section>
        </div>

        {/* Sidebar booking */}
        <aside className="lg:sticky lg:top-20 self-start">
          <div className="paper-card p-6" data-testid={BOOKING.createForm}>
            <div className="flex items-center gap-2 mb-3">
              <CalendarClock className="w-4 h-4 text-sage-600" />
              <h2 className="font-heading font-semibold text-sage-900">Book {sitter.name?.split(" ")[0]}</h2>
            </div>

            {user?.role !== "family" ? (
              <p className="text-sm text-stone2-500">Log in as a family to book this sitter.</p>
            ) : (
              <form onSubmit={submitBooking} className="space-y-3">
                <div>
                  <label className="field-label">Start</label>
                  <input type="datetime-local" required value={start} onChange={(e) => setStart(e.target.value)}
                    data-testid="booking-start"
                    className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="field-label">End</label>
                  <input type="datetime-local" required value={end} onChange={(e) => setEnd(e.target.value)}
                    data-testid="booking-end"
                    className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm" />
                </div>

                {children.length > 0 && (
                  <div>
                    <label className="field-label">Children</label>
                    <div className="space-y-1.5">
                      {children.map((c) => (
                        <label key={c.id} className="flex items-center gap-2 text-sm text-sage-900">
                          <input type="checkbox" checked={selectedKids.includes(c.id)}
                            onChange={(e) => {
                              setSelectedKids((s) => e.target.checked ? [...s, c.id] : s.filter((x) => x !== c.id));
                            }}
                            data-testid={`booking-child-${c.id}`}
                            className="rounded text-sage-600 focus:ring-sage-500" />
                          <Baby className="w-3.5 h-3.5" /> {c.name} (age {c.age})
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="field-label">Notes for sitter</label>
                  <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                    data-testid="booking-notes"
                    className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm"
                    placeholder="Any specifics for this visit..." />
                </div>

                <div className="rounded-lg border border-stone2-200 bg-white p-3">
                  <label className="flex items-center gap-2 text-sm text-sage-900">
                    <input type="checkbox" checked={isRecurring}
                      onChange={(e) => setIsRecurring(e.target.checked)}
                      data-testid="booking-recurring-toggle"
                      className="rounded text-sage-600 focus:ring-sage-500" />
                    <span className="font-semibold">Recurring booking</span>
                  </label>
                  {isRecurring && (
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <div>
                        <label className="field-label">Pattern</label>
                        <select value={pattern} onChange={(e) => setPattern(e.target.value)}
                          data-testid="booking-recurrence-pattern"
                          className="w-full rounded-lg border border-stone2-200 bg-white px-2 py-1.5 text-sm">
                          <option value="weekly">Weekly</option>
                          <option value="biweekly">Bi-weekly</option>
                          <option value="monthly">Every 4 weeks</option>
                        </select>
                      </div>
                      <div>
                        <label className="field-label">Occurrences</label>
                        <input type="number" min="1" max="26" value={occurrences}
                          onChange={(e) => setOccurrences(e.target.value)}
                          data-testid="booking-recurrence-occurrences"
                          className="w-full rounded-lg border border-stone2-200 bg-white px-2 py-1.5 text-sm" />
                      </div>
                    </div>
                  )}
                </div>

                {hours > 0 && (
                  <div className="rounded-lg bg-sage-50 p-3 text-sm">
                    <div className="flex justify-between"><span className="text-stone2-600">{hours.toFixed(2)} h × ${sitter.hourly_rate}</span><span className="text-sage-900 font-semibold">${subtotal.toFixed(2)}</span></div>
                    <div className="flex justify-between"><span className="text-stone2-600">Service fee (10%)</span><span className="text-sage-900">${fee.toFixed(2)}</span></div>
                    <div className="flex justify-between mt-1 pt-2 border-t border-stone2-200"><span className="font-semibold text-sage-900">Total per session</span><span className="font-heading font-bold text-sage-900">${total.toFixed(2)}</span></div>
                    {isRecurring && (
                      <div className="flex justify-between mt-1 text-xs text-stone2-600">
                        <span>× {occurrences} occurrences</span>
                        <span className="font-semibold text-sage-900">${(total * Number(occurrences)).toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                )}

                <button type="submit" disabled={booking} data-testid={SITTER_DETAIL.bookBtn}
                  className="btn-primary w-full justify-center">
                  {booking ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />}
                  {isRecurring ? `Request ${occurrences} bookings` : "Send booking request"}
                </button>

                <button type="button" onClick={joinWaitlist} disabled={joiningWaitlist}
                  data-testid="booking-waitlist-btn"
                  className="btn-outline-sage w-full justify-center text-xs">
                  {joiningWaitlist ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Users className="w-3.5 h-3.5" />}
                  Add me to waitlist instead
                </button>
              </form>
            )}

            <button type="button" onClick={startChat} data-testid={SITTER_DETAIL.messageBtn}
              className="btn-outline-sage w-full justify-center mt-3 text-sm">
              <MessageSquare className="w-4 h-4" /> Message {sitter.name?.split(" ")[0]}
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
